import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  Paper,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  MilitaryTech as MedalIcon,
  Speed as SpeedIcon,
  TrendingUp as TrendingUpIcon,
  Star as StarIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import axios from 'axios';

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (
    typeof window !== 'undefined' &&
    (window.location.hostname.includes('ondigitalocean.app') ||
      window.location.hostname.includes('services.knls.in'))
  ) {
    return '/api';
  }
  return 'http://localhost:3000/api';
};

const API_BASE_URL = getApiBaseUrl();

const DEFAULT_CATEGORIES = [
  { id: 'fingering', title: 'Fingering', group: 'lessons', emoji: '⌨️' },
  { id: 'words', title: 'Words', group: 'lessons', emoji: '🔤' },
  { id: 'sentences', title: 'Sentences', group: 'lessons', emoji: '📝' },
  { id: 'paragraph', title: 'Paragraph', group: 'lessons', emoji: '📄' },
  { id: 'stories', title: 'Stories', group: 'lessons', emoji: '📖' },
  { id: 'speed_blitz', title: 'Speed Blitz', group: 'games', emoji: '⚡' },
  { id: 'key_pop', title: 'Key Pop', group: 'games', emoji: '🫧' },
  { id: 'type_racer', title: 'Type Racer', group: 'games', emoji: '🏃' },
];

const sortByMastery = (a, b) => {
  const masteryA =
    parseFloat(a.masteryScore) ||
    (parseFloat(a.highestSpeed || 0) * parseFloat(a.accuracy || 0)) / 100;
  const masteryB =
    parseFloat(b.masteryScore) ||
    (parseFloat(b.highestSpeed || 0) * parseFloat(b.accuracy || 0)) / 100;
  if (masteryB !== masteryA) return masteryB - masteryA;
  const accA = parseFloat(a.accuracy || 0);
  const accB = parseFloat(b.accuracy || 0);
  if (accB !== accA) return accB - accA;
  return parseFloat(b.highestSpeed || 0) - parseFloat(a.highestSpeed || 0);
};

const formatAchievedAt = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const RankCard = ({
  student,
  rank,
  variant,
  isMobile,
  isCurrentStudent,
}) => {
  const isWinner = variant === 'winner';
  const isRunner = variant === 'runner';
  const achievedLabel = formatAchievedAt(student.achievedAt);

  return (
    <Card
      sx={{
        mb: 2,
        background: isWinner
          ? 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)'
          : isRunner
            ? 'linear-gradient(135deg, #C0C0C0 0%, #A8A8A8 100%)'
            : undefined,
        color: isWinner ? 'black' : isRunner ? 'white' : 'inherit',
        boxShadow: isWinner
          ? '0 8px 32px rgba(255, 215, 0, 0.4)'
          : isRunner
            ? '0 6px 24px rgba(192, 192, 192, 0.3)'
            : undefined,
        border: isWinner ? '3px solid #FFD700' : isRunner ? '2px solid #C0C0C0' : undefined,
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ position: 'relative' }}>
            <Avatar
              sx={{
                width: { xs: isWinner ? 60 : 50, sm: isWinner ? 80 : 70 },
                height: { xs: isWinner ? 60 : 50, sm: isWinner ? 80 : 70 },
                bgcolor: isWinner ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.3)',
                color: isWinner ? 'black' : 'white',
                fontSize: { xs: 20, sm: 28 },
                border: isWinner ? '3px solid black' : '2px solid white',
              }}
            >
              {student.studentName?.charAt(0)?.toUpperCase() || '?'}
            </Avatar>
            <Box
              sx={{
                position: 'absolute',
                top: -8,
                right: -8,
                bgcolor: isWinner ? '#FFD700' : '#C0C0C0',
                borderRadius: '50%',
                p: 0.5,
                border: isWinner ? '2px solid black' : undefined,
              }}
            >
              {isWinner ? (
                <TrophyIcon sx={{ fontSize: { xs: 22, sm: 28 }, color: 'black' }} />
              ) : (
                <MedalIcon sx={{ fontSize: { xs: 20, sm: 26 }, color: 'white' }} />
              )}
            </Box>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Chip
              label={isWinner ? '🥇 WINNER' : '🥈 RUNNER-UP'}
              sx={{
                bgcolor: isWinner ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.3)',
                color: isWinner ? 'black' : 'white',
                fontWeight: 'bold',
                mb: 1,
                fontSize: { xs: '0.7rem', sm: '0.875rem' },
              }}
            />
            <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="bold" gutterBottom>
              {student.studentName || 'Unknown'}
              {isCurrentStudent(student.phone) && (
                <StarIcon sx={{ ml: 1, fontSize: 18, verticalAlign: 'middle' }} />
              )}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <SpeedIcon />
              <Typography variant={isMobile ? 'h6' : isWinner ? 'h4' : 'h5'} fontWeight="bold">
                {student.highestSpeed || 0} WPM
              </Typography>
              {student.accuracy != null && (
                <Chip
                  label={`${Math.round(student.accuracy)}% Acc`}
                  size="small"
                  sx={{
                    bgcolor: isWinner ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.35)',
                    color: isWinner ? 'black' : 'white',
                    fontWeight: 'bold',
                  }}
                />
              )}
            </Box>
            {achievedLabel && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75 }}>
                <ScheduleIcon sx={{ fontSize: 16, opacity: 0.85 }} />
                <Typography
                  variant="body2"
                  sx={{ opacity: 0.9, fontWeight: 500, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  Achieved: {achievedLabel}
                </Typography>
              </Box>
            )}
          </Box>
          <Box
            sx={{
              textAlign: 'center',
              bgcolor: isWinner ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.2)',
              borderRadius: 2,
              p: isWinner ? 2 : 1.5,
              minWidth: { xs: 70, sm: 90 },
            }}
          >
            <Typography variant="caption" sx={{ opacity: 0.9, display: 'block', mb: 0.5 }}>
              Rank
            </Typography>
            <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight="bold">
              #{rank}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const DailySpeedLeaderboard = ({ currentStudentPhone }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [activeCategoryId, setActiveCategoryId] = useState('fingering');
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const fetchLeaderboard = async () => {
    try {
      setLoading(true);
      setError(null);

      const today = new Date().toISOString().split('T')[0];
      const response = await axios.get(`${API_BASE_URL}/track-sheets/leaderboard/${today}`);

      if (response.data.success) {
        let cats = response.data.categories;
        if (!cats || !cats.length) {
          // Legacy API: single flat list — show as Overall only
          const flat = (response.data.leaderboard || []).slice().sort(sortByMastery);
          cats = [
            {
              id: 'overall',
              title: 'Overall',
              group: 'lessons',
              emoji: '🏆',
              entries: flat,
              winner: flat[0] || null,
              participantCount: flat.length,
            },
          ];
        } else {
          cats = cats.map((cat) => ({
            ...cat,
            entries: (cat.entries || []).slice().sort(sortByMastery),
          }));
        }
        setCategories(cats);
        const firstWithPeople = cats.find((c) => (c.entries || []).length > 0);
        setActiveCategoryId((firstWithPeople || cats[0]).id);
      } else {
        setCategories(DEFAULT_CATEGORIES.map((c) => ({ ...c, entries: [], winner: null })));
      }
    } catch (err) {
      console.error('Error fetching leaderboard:', err);
      setError('Failed to load leaderboard');
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const getRankColor = (index) => {
    if (index === 0) return '#FFD700';
    if (index === 1) return '#C0C0C0';
    return '#CD7F32';
  };

  const isCurrentStudent = (phone) => phone === currentStudentPhone;

  const lessonTabs = useMemo(
    () => categories.filter((c) => c.group === 'lessons' || c.id === 'overall'),
    [categories],
  );
  const gameTabs = useMemo(
    () => categories.filter((c) => c.group === 'games'),
    [categories],
  );

  const activeCategory =
    categories.find((c) => c.id === activeCategoryId) || categories[0] || null;
  const leaderboard = activeCategory?.entries || [];
  const winner = leaderboard[0];
  const runnerUp = leaderboard[1];
  const remaining = leaderboard.slice(2);
  const hasAnyPractice = categories.some((c) => (c.entries || []).length > 0);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Card
        sx={{
          mb: 2,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <TrendingUpIcon sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                🏆 Daily Typing Leaderboard
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Separate winners per lesson & game · Typing Tutor practice · speed × accuracy
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Lesson tracks */}
      {lessonTabs.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ px: 1, fontWeight: 600 }}>
            LESSONS
          </Typography>
          <Tabs
            value={lessonTabs.some((t) => t.id === activeCategoryId) ? activeCategoryId : false}
            onChange={(_, v) => setActiveCategoryId(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 42,
              '& .MuiTab-root': { minHeight: 42, textTransform: 'none', fontWeight: 600 },
            }}
          >
            {lessonTabs.map((cat) => (
              <Tab
                key={cat.id}
                value={cat.id}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <span>{cat.emoji}</span>
                    <span>{cat.title}</span>
                    {(cat.participantCount || cat.entries?.length || 0) > 0 && (
                      <Chip
                        size="small"
                        label={cat.participantCount || cat.entries.length}
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>
                }
              />
            ))}
          </Tabs>
        </Box>
      )}

      {/* Games */}
      {gameTabs.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ px: 1, fontWeight: 600 }}>
            GAMES
          </Typography>
          <Tabs
            value={gameTabs.some((t) => t.id === activeCategoryId) ? activeCategoryId : false}
            onChange={(_, v) => setActiveCategoryId(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 42,
              '& .MuiTab-root': { minHeight: 42, textTransform: 'none', fontWeight: 600 },
            }}
          >
            {gameTabs.map((cat) => (
              <Tab
                key={cat.id}
                value={cat.id}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <span>{cat.emoji}</span>
                    <span>{cat.title}</span>
                    {(cat.participantCount || cat.entries?.length || 0) > 0 && (
                      <Chip
                        size="small"
                        label={cat.participantCount || cat.entries.length}
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>
                }
              />
            ))}
          </Tabs>
        </Box>
      )}

      {!hasAnyPractice || leaderboard.length === 0 ? (
        <Card
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
          }}
        >
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <SpeedIcon sx={{ fontSize: 60, mb: 2, opacity: 0.7 }} />
            <Typography variant="h6" gutterBottom>
              {activeCategory
                ? `No ${activeCategory.title} records today`
                : 'No Speed Records Today'}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Practice this track on Typing Tutor to appear here.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <span>{activeCategory?.emoji}</span>
            {activeCategory?.title} · Today&apos;s rankings
          </Typography>

          {winner && (
            <RankCard
              student={winner}
              rank={1}
              variant="winner"
              isMobile={isMobile}
              isCurrentStudent={isCurrentStudent}
            />
          )}
          {runnerUp && (
            <RankCard
              student={runnerUp}
              rank={2}
              variant="runner"
              isMobile={isMobile}
              isCurrentStudent={isCurrentStudent}
            />
          )}

          {remaining.length > 0 && (
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                  Other Participants
                </Typography>
                <Grid container spacing={2}>
                  {remaining.map((student, index) => (
                    <Grid item xs={12} sm={6} md={4} key={student.phone || index}>
                      <Paper
                        elevation={isCurrentStudent(student.phone) ? 4 : 1}
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          border: isCurrentStudent(student.phone)
                            ? '2px solid #667eea'
                            : '1px solid #e0e0e0',
                          bgcolor: isCurrentStudent(student.phone) ? '#f3f4ff' : 'white',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box
                            sx={{
                              minWidth: 40,
                              height: 40,
                              borderRadius: '50%',
                              bgcolor: getRankColor(index + 2) + '20',
                              color: getRankColor(index + 2),
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 'bold',
                              fontSize: '0.875rem',
                            }}
                          >
                            #{index + 3}
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="body1"
                              fontWeight={isCurrentStudent(student.phone) ? 'bold' : 'medium'}
                              noWrap
                            >
                              {student.studentName || 'Unknown'}
                              {isCurrentStudent(student.phone) && (
                                <StarIcon sx={{ fontSize: 16, color: '#667eea', ml: 0.5 }} />
                              )}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {student.highestSpeed || 0} WPM
                              {student.accuracy != null
                                ? ` · ${Math.round(student.accuracy)}% Acc`
                                : ''}
                            </Typography>
                            {formatAchievedAt(student.achievedAt) && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                {formatAchievedAt(student.achievedAt)}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Box>
  );
};

export default DailySpeedLeaderboard;
